import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z, type ZodTypeAny } from "zod/v3";
import { kernelQuery, getWorkspaceId, getWorkspaceContext, getAuditLog, getAgentSessionId, getApiKeyScope, isSessionOriented, type AuditEntry } from "../client.js";
import { success, successResult } from "../envelope.js";

type CallCategory = "read" | "search" | "write" | "label" | "meta";

const CALL_CATEGORIES: Record<string, CallCategory> = {
  "chain.getEntry": "read",
  "chain.batchGetEntries": "read",
  "chain.listEntries": "read",
  "chain.listEntryHistory": "read",
  "chain.listEntryRelations": "read",
  "chain.listEntriesByLabel": "read",
  "chain.searchEntries": "search",
  "chain.createEntry": "write",
  "chain.updateEntry": "write",
  "chain.moveToCollection": "write",
  "chain.createEntryRelation": "write",
  "chain.applyLabel": "label",
  "chain.removeLabel": "label",
  "chain.createLabel": "label",
  "chain.updateLabel": "label",
  "chain.deleteLabel": "label",
  "chain.createCollection": "write",
  "chain.updateCollection": "write",
  "chain.listCollections": "meta",
  "chain.getCollection": "meta",
  "chain.listLabels": "meta",
  "resolveWorkspace": "meta",
};

function categorize(fn: string): CallCategory {
  return CALL_CATEGORIES[fn] ?? "meta";
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

function buildSessionSummary(log: readonly AuditEntry[]): string {
  if (log.length === 0) return "";

  const byCategory = new Map<CallCategory, Map<string, number>>();
  let errorCount = 0;
  let writeCreates = 0;
  let writeUpdates = 0;

  for (const entry of log) {
    const cat = categorize(entry.fn);
    if (!byCategory.has(cat)) byCategory.set(cat, new Map());
    const fnCounts = byCategory.get(cat)!;
    fnCounts.set(entry.fn, (fnCounts.get(entry.fn) ?? 0) + 1);

    if (entry.status === "error") errorCount++;
    if (entry.fn === "chain.createEntry" && entry.status === "ok") writeCreates++;
    if (entry.fn === "chain.updateEntry" && entry.status === "ok") writeUpdates++;
  }

  const firstTs = new Date(log[0].ts).getTime();
  const lastTs = new Date(log[log.length - 1].ts).getTime();
  const duration = formatDuration(lastTs - firstTs);

  const lines: string[] = [`# Session Summary (${duration})\n`];

  const categoryLabels: [CallCategory, string][] = [
    ["read", "Reads"],
    ["search", "Searches"],
    ["write", "Writes"],
    ["label", "Labels"],
    ["meta", "Meta"],
  ];

  for (const [cat, label] of categoryLabels) {
    const fnCounts = byCategory.get(cat);
    if (!fnCounts || fnCounts.size === 0) continue;
    const total = [...fnCounts.values()].reduce((a, b) => a + b, 0);
    const detail = [...fnCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([fn, count]) => `${fn.replace("chain.", "")} x${count}`)
      .join(", ");
    lines.push(`- **${label}:** ${total} call${total === 1 ? "" : "s"} (${detail})`);
  }

  lines.push(`- **Errors:** ${errorCount}`);

  if (writeCreates > 0 || writeUpdates > 0) {
    lines.push("");
    lines.push("## Knowledge Contribution");
    if (writeCreates > 0) lines.push(`- ${writeCreates} entr${writeCreates === 1 ? "y" : "ies"} created`);
    if (writeUpdates > 0) lines.push(`- ${writeUpdates} entr${writeUpdates === 1 ? "y" : "ies"} updated`);
  }

  return lines.join("\n");
}

// ─── Organisation Health (FEAT-129) ─────────────────────────────────────────

export type OrgHealthFlag = {
  collection: string;
  count: number;
  suggestedCollection: string;
};

export type OrgHealthResult = {
  reviewed: number;
  agreements: number;
  disagreements: number;
  abstentions: number;
  agreementRate: number;
  flags: OrgHealthFlag[];
};

/**
 * Compute org health from pre-classified results (from Convex batch heuristic).
 * TEN-174 resolution: classification runs in Convex with DB-driven signals,
 * not locally with a stale copy. Zero drift risk.
 */
export function computeOrganisationHealth(
  entries: Array<{ collectionSlug?: string; collection?: string }>,
  classifications: Array<{ collection: string | null; confidence: number }>,
): OrgHealthResult {
  let agreements = 0;
  let disagreements = 0;
  let abstentions = 0;
  const flagMap = new Map<string, Map<string, number>>();

  for (let i = 0; i < entries.length; i++) {
    const slug = entries[i].collectionSlug ?? entries[i].collection ?? "unknown";
    const result = classifications[i];

    if (!result?.collection) {
      abstentions++;
      continue;
    }

    if (result.collection === slug) {
      agreements++;
    } else {
      disagreements++;
      if (!flagMap.has(slug)) flagMap.set(slug, new Map());
      const suggestions = flagMap.get(slug)!;
      suggestions.set(result.collection, (suggestions.get(result.collection) ?? 0) + 1);
    }
  }

  const opinionated = agreements + disagreements;
  const agreementRate = opinionated > 0 ? Math.round((agreements / opinionated) * 100) : 100;

  const flags = [...flagMap.entries()]
    .map(([collection, suggestions]) => {
      const total = [...suggestions.values()].reduce((a, b) => a + b, 0);
      const topSuggested = [...suggestions.entries()].sort((a, b) => b[1] - a[1])[0];
      return { collection, count: total, suggestedCollection: topSuggested?.[0] ?? "unknown" };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  return { reviewed: entries.length, agreements, disagreements, abstentions, agreementRate, flags };
}

export function formatOrgHealthLines(orgHealth: OrgHealthResult, maxFlags = 3): string[] {
  const lines: string[] = [];
  if (orgHealth.disagreements > 0) {
    lines.push(
      `${orgHealth.disagreements} of ${orgHealth.reviewed} entries flagged for review (${orgHealth.agreementRate}% classifier agreement).`,
    );
    for (const flag of orgHealth.flags.slice(0, maxFlags)) {
      lines.push(`- **${flag.collection}**: ${flag.count} entries may belong in \`${flag.suggestedCollection}\``);
    }
  } else if (orgHealth.reviewed > 0) {
    lines.push(`All ${orgHealth.reviewed - orgHealth.abstentions} classified entries agree with stored collection (${orgHealth.abstentions} without coverage).`);
  }
  return lines;
}

async function fetchOrganisationHealth(): Promise<OrgHealthResult | null> {
  try {
    const allEntries = await kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>(
      "chain.listEntries",
      { fields: ["name", "description", "collectionSlug"] },
    );
    if (!allEntries || allEntries.length === 0) return null;

    // description is projected server-side from data.description (Task 3), so it
    // arrives top-level here — no e.data blob crosses the wire. `chain.listEntries`'s
    // return type is a union (raw EntryDoc[] when no `fields` requested, vs
    // Record<string, unknown>[] when they are, see wrapup.ts's doc comment on this
    // recurring residual) — this call DOES request `fields`, so runtime is provably the
    // projected branch, but property access still checks against the whole union
    // (PR #395, Lane C S3b annotation pass).
    const projectedEntries = allEntries as Array<{ name?: string; description?: unknown; collectionSlug?: string }>;
    const classifyInput = projectedEntries.map((e) => ({
      name: e.name ?? "",
      description: typeof e.description === "string" ? e.description : "",
    }));
    const classifications = await kernelQuery<GatewayRouteReturnByName<"chain.batchClassifyHeuristic">>(
      "chain.batchClassifyHeuristic",
      { entries: classifyInput },
    );

    return computeOrganisationHealth(projectedEntries, classifications);
  } catch (err) {
    process.stderr.write(`[MCP] fetchOrganisationHealth failed: ${(err as Error).message}\n`);
    return null;
  }
}

export async function handleHealthCheck() {
  const start = Date.now();
  const errors: string[] = [];

  let workspaceId: string | undefined;
  try {
    workspaceId = await getWorkspaceId();
  } catch (e: unknown) {
    errors.push(`Workspace resolution failed: ${e instanceof Error ? e.message : String(e)}`);
  }

  let collections: unknown[] = [];
  try {
    collections = await kernelQuery<unknown[]>("chain.listCollections");
  } catch (e: unknown) {
    errors.push(`Collection fetch failed: ${e instanceof Error ? e.message : String(e)}`);
  }

  let totalEntries = 0;
  if (collections.length > 0) {
    try {
      const entries = await kernelQuery<GatewayRouteReturnByName<"chain.listEntries">>(
        "chain.listEntries",
        { fields: ["entryId"] },
      );
      totalEntries = (entries ?? []).length;
    } catch (e: unknown) {
      errors.push(`Entry count failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  let wsCtx: { workspaceSlug: string; workspaceName: string } | null = null;
  try {
    wsCtx = await getWorkspaceContext();
  } catch { /* already captured workspace error above */ }

  const durationMs = Date.now() - start;
  const healthy = errors.length === 0;

  const lines = [
    `# ${healthy ? "Healthy" : "Degraded"}`,
    "",
    `**Workspace:** ${workspaceId ?? "unresolved"}`,
    `**Workspace Slug:** ${wsCtx?.workspaceSlug ?? "unknown"}`,
    `**Workspace Name:** ${wsCtx?.workspaceName ?? "unknown"}`,
    `**Collections:** ${collections.length}`,
    `**Entries:** ${totalEntries}`,
    `**Latency:** ${durationMs}ms`,
  ];

  if (errors.length > 0) {
    lines.push("", "## Errors");
    for (const err of errors) {
      lines.push(`- ${err}`);
    }
  }

  const healthData = {
    healthy,
    collections: collections.length,
    entries: totalEntries,
    latencyMs: durationMs,
    workspace: workspaceId ?? "unresolved",
  };

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      healthy ? `Healthy. ${collections.length} collections, ${totalEntries} entries, ${durationMs}ms.` : `Degraded. ${errors.length} error(s).`,
      healthData,
    ),
  };
}

export async function handleWhoami() {
  const ctx = await getWorkspaceContext();
  const sessionId = getAgentSessionId();
  const scope = getApiKeyScope();
  const oriented = isSessionOriented();
  const lines = [
    `# Session Identity`,
    "",
    `**Workspace ID:** ${ctx.workspaceId}`,
    `**Workspace Slug:** ${ctx.workspaceSlug}`,
    `**Workspace Name:** ${ctx.workspaceName}`,
  ];
  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Session: ${ctx.workspaceName} (${scope}). ${oriented ? "Oriented." : "Not oriented."}`,
      { workspaceId: ctx.workspaceId, workspaceName: ctx.workspaceName, scope, sessionId, oriented },
    ),
  };
}

const STAGE_LABELS: Record<string, string> = {
  blank:     "Blank",
  seeded:    "Seeded",
  grounded:  "Grounded",
  connected: "Connected",
};

const STAGE_DESCRIPTIONS: Record<string, string> = {
  blank:     "No knowledge captured yet.",
  seeded:    "Early knowledge is in place — keep building.",
  grounded:  "Solid foundations — a few gaps remain.",
  connected: "Well-connected knowledge graph — your Brain is useful.",
};

export async function handleWorkspaceStatus() {
  const result = await kernelQuery<GatewayRouteReturnByName<"chain.workspaceReadiness">>("chain.workspaceReadiness");

  const { score, totalChecks, passedChecks, checks, gaps, stats, governanceMode } = result;
  // Guard against older backends that predate v2 scoring.
  const scoringVersion: "v1" | "v2" = result.scoringVersion ?? "v1";
  const stage: "blank" | "seeded" | "grounded" | "connected" = result.stage ?? "seeded";

  const stageLabel = STAGE_LABELS[stage] ?? stage;
  const stageDescription = STAGE_DESCRIPTIONS[stage] ?? "";
  const scoreBar = "█".repeat(Math.round(score / 10)) + "░".repeat(10 - Math.round(score / 10));

  const lines = [
    `# Brain Status: ${stageLabel}`,
    `_${stageDescription}_`,
    "",
    `${scoreBar} ${stageLabel} · ${score}%`,
    `**Governance:** ${governanceMode ?? "open"}${(governanceMode ?? "open") !== "open" ? " (accepts require proposal)" : ""}`,
    "",
    "## Stats",
    `- **Entries:** ${stats.totalEntries} (${stats.activeCount} active, ${stats.draftCount} draft)`,
    `- **Relations:** ${stats.totalRelations}`,
    `- **Collections:** ${stats.collectionCount}`,
    `- **Orphaned:** ${stats.orphanedCount} committed entries with no relations`,
    "",
  ];

  if (gaps.length > 0) {
    lines.push("## Gaps");
    for (const gap of gaps) {
      // capabilityGuidance is plain English (v2); fall back to guidance for backward compat
      const action = gap.capabilityGuidance ?? gap.guidance;
      lines.push(`- [ ] **${gap.label}**`);
      lines.push(`  _${action}_`);
    }
    lines.push("");
  }

  const passed = checks.filter((c) => c.passed);
  if (passed.length > 0) {
    lines.push("## Passing checks");
    for (const check of passed) {
      lines.push(`- [x] ${check.label} (${check.current}/${check.required})`);
    }
    lines.push("");
  }

  const orgHealth = await fetchOrganisationHealth();
  if (orgHealth && orgHealth.reviewed > 0) {
    lines.push("## Organisation Health");
    lines.push(...formatOrgHealthLines(orgHealth));
    lines.push("");
  }

  const statusData: Record<string, unknown> = {
    stage,
    scoringVersion,
    readinessScore: score,
    activeEntries: stats.activeCount,
    totalRelations: stats.totalRelations,
    orphanedEntries: stats.orphanedCount,
    gaps: gaps.map((g) => ({
      id: g.id,
      label: g.label,
      guidance: g.capabilityGuidance ?? g.guidance,
    })),
    ...(orgHealth && { organisationHealth: orgHealth }),
  };

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Brain: ${stageLabel} (${score}%). ${stats.activeCount} active entries, ${gaps.length} gap(s).`,
      statusData,
    ),
  };
}

export async function handleAudit(limit: number) {
  const log = getAuditLog();
  const recent = log.slice(-limit);

  if (recent.length === 0) {
    return successResult("No calls recorded yet this session.", "No calls recorded yet this session.", { totalCalls: 0, calls: [] });
  }

  const summary = buildSessionSummary(log);

  const logLines = [`# Audit Log (last ${recent.length} of ${log.length} total)\n`];
  for (const entry of recent) {
    const icon = entry.status === "ok" ? "\u2713" : "\u2717";
    const errPart = entry.error ? ` \u2014 ${entry.error}` : "";
    const toolPart = entry.toolContext
      ? ` [${entry.toolContext.tool}${entry.toolContext.action ? ` action=${entry.toolContext.action}` : ""}]`
      : "";
    logLines.push(`${icon} \`${entry.fn}\`${toolPart} ${entry.durationMs}ms ${entry.status}${errPart}`);
  }

  const auditData = {
    totalCalls: log.length,
    calls: recent.map((entry) => ({
      tool: entry.fn,
      ...(entry.toolContext?.action && { action: entry.toolContext.action }),
      timestamp: entry.ts,
      ...(entry.durationMs != null && { durationMs: entry.durationMs }),
    })),
  };

  return {
    content: [{ type: "text" as const, text: `${summary}\n\n---\n\n${logLines.join("\n")}` }],
    structuredContent: success(
      `Audit: ${log.length} total calls, showing last ${recent.length}.`,
      auditData,
    ),
  };
}

const HEALTH_ACTIONS = ["check", "whoami", "status", "audit", "self-test"] as const;
type HealthAction = (typeof HEALTH_ACTIONS)[number];

export const healthSchema = z.object({
  action: z.enum(HEALTH_ACTIONS).describe(
    "'check': connectivity and workspace stats. 'whoami': session identity. 'status': workspace readiness. 'audit': session audit log. 'self-test': validate all tool schemas.",
  ),
  limit: z.number().min(1).max(50).default(20).optional()
    .describe("For audit: how many recent calls to show (max 50)"),
});

export { orientSchema } from "./orient.js";

export const healthCheckOutputSchema = z.object({
  healthy: z.boolean(),
  collections: z.number(),
  entries: z.number(),
  latencyMs: z.number(),
  workspace: z.string(),
});

const organisationHealthSchema = z.object({
  reviewed: z.number(),
  agreements: z.number(),
  disagreements: z.number(),
  abstentions: z.number(),
  agreementRate: z.number(),
  flags: z.array(z.object({
    collection: z.string(),
    count: z.number(),
    suggestedCollection: z.string(),
  })),
});

export const healthStatusOutputSchema = z.object({
  stage: z.enum(["blank", "seeded", "grounded", "connected"]).optional().default("seeded"),
  scoringVersion: z.enum(["v1", "v2"]).optional().default("v1"),
  readinessScore: z.number(),
  activeEntries: z.number(),
  totalRelations: z.number(),
  orphanedEntries: z.number(),
  gaps: z.array(z.object({ id: z.string(), label: z.string(), guidance: z.string() })),
  organisationHealth: organisationHealthSchema.optional(),
});

export const healthAuditOutputSchema = z.object({
  totalCalls: z.number(),
  calls: z.array(z.object({
    tool: z.string(),
    action: z.string().optional(),
    timestamp: z.string(),
    durationMs: z.number().optional(),
  })),
});

export const healthWhoamiOutputSchema = z.object({
  workspaceId: z.string(),
  workspaceName: z.string(),
  scope: z.string(),
  sessionId: z.union([z.string(), z.null()]),
  oriented: z.boolean(),
});

export const selfTestOutputSchema = z.object({
  passed: z.number(),
  failed: z.number(),
  total: z.number(),
  results: z.array(z.object({
    tool: z.string(),
    valid: z.boolean(),
    error: z.string().optional(),
  })),
});

/**
 * TEN-2032 (S3): derive the self-test list from the LIVE tool registry
 * instead of a hand-maintained array. The old `ALL_TOOL_SCHEMAS` const
 * drifted from actual registrations (stale at 31 tools while 37 were live,
 * per server.ts's pre-S3 comment) — deriving from `_registeredTools`
 * structurally can't drift, because it reads the same map `tools/list`
 * serves. `_registeredTools` is a private SDK field (mcp.d.ts); the narrow
 * cast is explicit so a future SDK rename fails loudly at compile time
 * instead of silently returning `undefined`.
 */
export function handleSelfTest(server: McpServer) {
  const registry = (server as unknown as {
    _registeredTools: Record<string, { inputSchema?: ZodTypeAny }>;
  })._registeredTools;

  const results: Array<{ tool: string; valid: boolean; error?: string }> = [];

  for (const [name, tool] of Object.entries(registry)) {
    const schema = tool.inputSchema;
    try {
      if (!schema || typeof schema.safeParse !== "function") {
        results.push({ tool: name, valid: false, error: "Schema is not a valid Zod object" });
        continue;
      }
      const test = schema.safeParse({});
      // Even if parse fails (expected for required fields), the schema itself is valid
      if (test.success || test.error) {
        results.push({ tool: name, valid: true });
      }
    } catch (e: unknown) {
      results.push({ tool: name, valid: false, error: e instanceof Error ? e.message : String(e) });
    }
  }

  const passed = results.filter((r) => r.valid).length;
  const failed = results.filter((r) => !r.valid).length;
  const total = results.length;

  const lines: string[] = [
    `# Self-Test: Tool Schema Validation`,
    `**Result:** ${failed === 0 ? "ALL PASS" : `${failed} FAILED`}`,
    `**Schemas validated:** ${passed}/${total}`,
    "",
  ];

  if (failed > 0) {
    lines.push("## Failures");
    for (const r of results.filter((r) => !r.valid)) {
      lines.push(`- **${r.tool}**: ${r.error}`);
    }
    lines.push("");
  }

  lines.push("## All Tools");
  for (const r of results) {
    lines.push(`- ${r.valid ? "PASS" : "FAIL"} \`${r.tool}\``);
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      failed === 0 ? `Self-test: all ${total} schemas valid.` : `Self-test: ${failed}/${total} schemas failed.`,
      { passed, failed, total, results },
    ),
  };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `health` tool retired
// here. handleHealthCheck / handleWhoami / handleWorkspaceStatus /
// handleAudit / handleSelfTest stay live — workspace.ts (the `workspace`
// compound) is their only remaining caller. handleSelfTest now takes the
// live `server` so it can read the registry directly (TEN-2032, above).
